<template>
  <section class="feature dark" id="feature">
    <div class="container">
      <div class="row align-items-center">
        <div class="col-md-5">
          <section-title
            align="left"
            tone="dark"
            data-aos="fade-right"
            :kicker="t('Enjoy Unlimited Benefits With Just A Single Price!')"
            :title="t('Why pay monthly?')"
            :lede="lede"
          />
        </div>
        <div class="col-md-6 offset-md-1">
          <div class="row" data-aos="fade-left">
            <div class="col-6 col-sm-4 col-md-6 col-lg-4">
              <font-awesome-icon icon="hdd" />
              <h3>
                {{ t('Fully') }}<br />
                {{ t('Managed') }}
              </h3>
            </div>
            <div class="col-6 col-sm-4 col-md-6 col-lg-4">
              <font-awesome-icon icon="piggy-bank" />
              <h3>
                {{ t('Low') }}<br />
                {{ t('Startup Cost') }}
              </h3>
            </div>
            <div class="col-6 col-sm-4 col-md-6 col-lg-4">
              <font-awesome-icon icon="life-ring" />
              <h3>
                {{ t('Lifetime') }}<br />
                {{ t('Support') }}
              </h3>
            </div>
            <div class="col-6 col-sm-4 col-md-6 col-lg-4">
              <font-awesome-icon icon="heart-pulse" />
              <h3>
                {{ t('24 Hours') }}<br />
                {{ t('Monitoring') }}
              </h3>
            </div>
            <div class="col-6 col-sm-4 col-md-6 col-lg-4">
              <font-awesome-icon icon="sync" />
              <h3>
                {{ t('Always') }}<br />
                {{ t('Up To Date') }}
              </h3>
            </div>
            <div class="col-6 col-sm-4 col-md-6 col-lg-4">
              <font-awesome-icon icon="cloud-upload-alt" />
              <h3>
                {{ t('Routine') }}<br />
                {{ t('Backup') }}
              </h3>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The left column is a SectionTitle now. It used to be three hand-rolled
   elements: an h5 kicker at $text-muted, an h1 with a Bootstrap mb-4, and a
   bare p that re-declared the dark lede spec. The kicker is the shared grey one
   and the lede is the shared dark lede, so both follow the site instead of
   being a fourth copy of it. All that is left here is the extra room the
   stacked layout needs below the copy on a phone, and the six tiles. */
.feature {
  :deep(.section-lede) {
    @media (max-width: 767.98px) {
      margin-bottom: 3rem;
    }
  }

  svg {
    align-items: center;
    border-radius: $r-lg;
    border: solid 1px $on-dark-line-strong;
    color: $on-dark-strong;
    display: flex;
    height: 80px;
    justify-content: center;
    margin-bottom: 1.5rem;
    padding: 1.5rem;
    position: relative;
    transition: background-color $dur $ease, border-color $dur $ease, transform $dur $ease;
    width: 80px;

    @media (max-width: 767.98px) {
      margin-left: auto;
      margin-right: auto;
    }
  }

  /* The six circles are the only thing in this section, and they were inert.
     Hovering the tile or its heading lifts the circle and firms up its outline,
     the same -3px + brighter border the services cards use. */
  .col-6:hover,
  .col-6:focus-within {
    svg {
      background: $on-dark-line;
      border-color: $on-dark-body;
      transform: translateY(-3px);
    }
  }

  h3 {
    color: $on-dark-strong;
    font-size: 1.25rem;
    line-height: 1.25em;
    margin-bottom: 3rem;

    @media (max-width: 767.98px) {
      text-align: center;
    }
  }
}
</style>

<script>
import { faCloudUploadAlt, faHdd, faHeartPulse, faLifeRing, faPiggyBank, faSync } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import SectionTitle from '@/Components/SectionTitle.vue';

FontAwesomeLibrary.add(faCloudUploadAlt, faHdd, faHeartPulse, faLifeRing, faPiggyBank, faSync);

export default {
  components: {
    SectionTitle
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          '24 Hours': '24 Hours',
          'Enjoy Unlimited Benefits With Just A Single Price!': 'Enjoy Unlimited Benefits With Just A Single Price!',
          'Startup Cost': 'Startup Cost',
          'Up To Date': 'Up To Date',
          'Why pay monthly?': 'Why pay monthly?',
          "Veximus gives you more than just web designs, our one-stop web subscription service provides everything you need to create impressive websites. Let's explore why you should join thousands of business owners who have opted to pay monthly for their business websites.": "Veximus gives you more than just web designs, our one-stop web subscription service provides everything you need to create impressive websites. Let's explore why you should join thousands of business owners who have opted to pay monthly for their business websites.",
          Always: 'Always',
          Backup: 'Backup',
          Fully: 'Fully',
          Lifetime: 'Lifetime',
          Low: 'Low',
          Managed: 'Managed',
          Monitoring: 'Monitoring',
          Routine: 'Routine',
          Support: 'Support'
        },
        'zh-MY': {
          '24 Hours': '24小时',
          'Enjoy Unlimited Benefits With Just A Single Price!': '只需一个价格即可享受无限的优惠！',
          'Startup Cost': '启动成本',
          'Up To Date': '版本更新',
          'Why pay monthly?': '为何按月支付？',
          "Veximus gives you more than just web designs, our one-stop web subscription service provides everything you need to create impressive websites. Let's explore why you should join thousands of business owners who have opted to pay monthly for their business websites.": '微星不仅仅为您提供了站点的开发与设计，我们的网络订阅服务更是提供了一站式的工具，让您有能力创造优秀、令人影响深刻的网站。如今，数以千计的业主已经选择了以每月订阅的模式来架构他们的企业网站，让我们来看一看为什么您也应该加入我们，成为每月服务订阅者的一员。',
          Always: '永久性',
          Backup: '云端备份',
          Fully: '伺服器',
          Lifetime: '终身性',
          Low: '超低廉',
          Managed: '全权托管',
          Monitoring: '实时监控',
          Routine: '周期性',
          Support: '技术支援'
        },
        'ms-MY': {
          '24 Hours': '24 Jam',
          'Enjoy Unlimited Benefits With Just A Single Price!': 'Manfaat Tanpa Had Dengan Satu Harga!',
          'Startup Cost': 'Rendah',
          'Up To Date': 'Kini',
          'Why pay monthly?': 'Kenapa bayar bulanan?',
          "Veximus gives you more than just web designs, our one-stop web subscription service provides everything you need to create impressive websites. Let's explore why you should join thousands of business owners who have opted to pay monthly for their business websites.": 'Veximus bukan hanya memberikan perkhidmatan reka bentuk web, kami menyediakan semua perkhidmatan yang anda perlu untuk membina laman web yang menarik. Tahukan kenapa ribuan pemilik perniagaan telah memilihi model bayaran bulanan untuk laman web perniagaan mereka.',
          Always: 'Dikemas',
          Backup: 'Sandaran',
          Fully: 'Diuruskan',
          Lifetime: 'Sokongan',
          Low: 'Kos',
          Managed: 'Sepenuhnya',
          Monitoring: 'Pemantauan',
          Routine: 'Rutin',
          Support: 'Selamanya'
        }
      }
    });

    /* The section lede is passed as a computed rather than as an inline t() in
       the template: the sentence contains an apostrophe, which cannot be quoted
       safely inside a Vue attribute, and a computed keeps it reactive to a
       locale change the way an inline call would. */
    const lede = computed(() => t("Veximus gives you more than just web designs, our one-stop web subscription service provides everything you need to create impressive websites. Let's explore why you should join thousands of business owners who have opted to pay monthly for their business websites."));

    return { t, lede };
  }
};
</script>
